"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeftRight, Search } from "lucide-react";
import dynamic from "next/dynamic";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import {
  useFieldArray,
  useForm,
  useWatch,
  type FieldErrors,
} from "react-hook-form";
import { toast } from "sonner";

import { Spinner } from "@/components/loader/spinner";
import { Button } from "@/components/ui/button";

import { TextShimmer } from "@/components/text-shimmer";
import { FlightFields } from "@/feature/flight/search/components/form/flight-fields";
import {
  DateUtils,
  FlightDateHelper,
  FlightFormHelper,
} from "@/feature/flight/search/lib/form";
import {
  FLIGHT_FARE_TYPE_OPTIONS,
  isRegularFlightFareType,
  type FlightFareType,
} from "@/feature/flight/search/lib/passenger-types";
import {
  createInitialData,
  generateSearchParams,
  getLastFlightSearch,
  saveLastFlightSearch,
} from "@/feature/flight/search/params/flight-search-persistence";
import { flightSearchSchema } from "@/feature/flight/search/schema/search";
import { toggleModifyBar } from "@/feature/flight/search/state/slices/flight-search-status.slice";
import {
  type IDecodedFlightSearch,
  type TFlightSearchFormData,
} from "@/feature/flight/search/types/search";
import { cn } from "@/lib/utils";
import { useAppDispatch } from "@/redux/hooks";

const FlightMultiCityFields = dynamic(
  () =>
    import("@/feature/flight/search/components/form/flight-multicity-fields").then(
      (mod) => mod.FlightMultiCityFields,
    ),
  {
    loading: () => (
      <div className="flex min-h-30 items-center justify-center py-6">
        <Spinner size="md" />
      </div>
    ),
  },
);

export interface IFlightSearchSubmitHandler {
  formdata: TFlightSearchFormData;
  searchParams: string;
}

interface FlightSearchFormProps {
  initialData?: IDecodedFlightSearch | null;
  submitHandler?: (data: IFlightSearchSubmitHandler) => Promise<void> | void;
}

const FARE_OPTIONS = [...FLIGHT_FARE_TYPE_OPTIONS] as const;

function getFlightSegmentLabel(index: number) {
  const labels = ["1st", "2nd", "3rd"];
  return `${labels[index] ?? `${index + 1}th`} Flight`;
}

const EMPTY_AIRPORT: TFlightSearchFormData["flights"][number]["from"] = {
  iata_code: "",
  name: "",
  city_name: "",
  country: "",
};

function collectErrorMessages(errors: unknown): string[] {
  if (!errors || typeof errors !== "object") {
    return [];
  }

  if (
    "message" in errors &&
    typeof errors.message === "string" &&
    errors.message.trim() !== ""
  ) {
    return [errors.message];
  }

  if (Array.isArray(errors)) {
    return errors.flatMap((error) => collectErrorMessages(error));
  }

  return Object.values(errors).flatMap((error) => collectErrorMessages(error));
}

function showSubmitErrorToast(errors: FieldErrors<TFlightSearchFormData>) {
  const messages = [...new Set(collectErrorMessages(errors))];

  if (messages.length === 0) {
    toast.error("Please fix the highlighted fields.", {
      description: "Review the form and try again.",
    });
    return;
  }

  toast.error("Please fix the following fields:", {
    description: (
      <div className="flex flex-col gap-1">
        {messages.map((message) => (
          <p key={message}>{message}</p>
        ))}
      </div>
    ),
  });
}

export default function FlightSearchForm({
  initialData: serverInitialData,
  submitHandler,
}: FlightSearchFormProps) {
  const dispatch = useAppDispatch();
  const [isPending, startTransition] = useTransition();

  const defaultValues = useMemo(
    () => createInitialData(serverInitialData),
    [serverInitialData],
  );

  const form = useForm<TFlightSearchFormData>({
    resolver: zodResolver(flightSearchSchema),
    defaultValues,
  });
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: "flights",
  });
  const flights = useWatch({
    control: form.control,
    name: "flights",
    defaultValue: defaultValues.flights,
  });
  const tripType = useWatch({
    control: form.control,
    name: "tripType",
    defaultValue: defaultValues.tripType,
  });
  const selectedFare = useWatch({
    control: form.control,
    name: "fareType",
    defaultValue: defaultValues.fareType,
  });
  const passengers = useWatch({
    control: form.control,
    name: "passengers",
    defaultValue: defaultValues.passengers,
  });
  const childrenAges = useWatch({
    control: form.control,
    name: "childrenAges",
    defaultValue: defaultValues.childrenAges,
  });
  const canRemoveMultiCityFlight =
    FlightFormHelper.canRemoveTFlightSegment(fields);

  const regularPassengerBackupRef = useRef<{
    passengers: TFlightSearchFormData["passengers"];
    childrenAges: TFlightSearchFormData["childrenAges"];
  } | null>(
    isRegularFlightFareType(defaultValues.fareType)
      ? {
          passengers: defaultValues.passengers,
          childrenAges: defaultValues.childrenAges,
        }
      : null,
  );
  const previousFareRef = useRef(selectedFare);

  const [swapFlipped, setSwapFlipped] = useState(false);
  useEffect(() => {
    if (serverInitialData) {
      return;
    }

    const storedSearch = getLastFlightSearch();
    if (!storedSearch) {
      return;
    }

    previousFareRef.current = storedSearch.fareType;
    regularPassengerBackupRef.current = isRegularFlightFareType(
      storedSearch.fareType,
    )
      ? {
          passengers: storedSearch.passengers,
          childrenAges: storedSearch.childrenAges,
        }
      : null;

    form.reset(storedSearch);
  }, [form, serverInitialData]);

  useEffect(() => {
    if (isRegularFlightFareType(selectedFare)) {
      regularPassengerBackupRef.current = {
        passengers,
        childrenAges,
      };
    }
  }, [childrenAges, passengers, selectedFare]);

  useEffect(() => {
    const previousFare = previousFareRef.current;

    if (previousFare === selectedFare) {
      return;
    }

    const wasRegular = isRegularFlightFareType(previousFare);
    const isRegular = isRegularFlightFareType(selectedFare);

    if (wasRegular && !isRegular) {
      const totalPassengers = FlightFormHelper.getTotalPassengers(
        form.getValues("passengers"),
      );

      form.setValue(
        "passengers",
        {
          adults: totalPassengers,
          children: 0,
          infants: 0,
        },
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
      form.setValue("childrenAges", [], {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
      form.clearErrors("childrenAges");
    }

    if (!wasRegular && isRegular && regularPassengerBackupRef.current) {
      form.setValue(
        "passengers",
        regularPassengerBackupRef.current.passengers,
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        },
      );
      form.setValue(
        "childrenAges",
        regularPassengerBackupRef.current.childrenAges,
        {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: false,
        },
      );
    }

    previousFareRef.current = selectedFare;
  }, [form, selectedFare]);

  /* Date Disable Logic */
  const getMinDate = useCallback(
    (index: number) => {
      const today = DateUtils.getToday();
      if (index <= 0) return today;
      for (let i = index - 1; i >= 0; i--) {
        const date = flights[i]?.departDate;
        if (date) return date;
      }
      return today;
    },
    [flights],
  );
  const isDateDisabled = useCallback(
    (index: number, date: Date) => {
      const minDate = getMinDate(index);
      return FlightDateHelper.isDateDisabled(date, minDate);
    },
    [getMinDate],
  );

  /* Handle Date Changes */
  const handleDateChange = useCallback(
    (index: number, newDate: Date | null | undefined) => {
      if (!newDate) return;
      const currentFlights = form.getValues("flights");
      const result = FlightDateHelper.getSubsequentFlightsToClear(
        currentFlights,
        index,
        newDate,
      );
      result.indicesToClear.forEach((i) => {
        form.setValue(`flights.${i}.departDate`, null, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      });

      const toastMessage = FlightDateHelper.getClearedDatesToastMessage(
        result.clearedCount,
      );

      if (toastMessage) {
        toast.info(toastMessage.message, {
          description: toastMessage.description,
        });
      }
    },
    [form],
  );

  /* Actions */
  const addFlight = useCallback(() => {
    const currentFlights = form.getValues("flights");
    if (!FlightFormHelper.canAddTFlightSegment(currentFlights)) return;
    append(FlightFormHelper.createNextTFlightSegment(currentFlights));
  }, [append, form]);

  const removeFlight = useCallback(
    (index: number) => {
      const currentFlights = form.getValues("flights");
      if (!FlightFormHelper.canRemoveTFlightSegment(currentFlights)) return;
      remove(index);
    },
    [form, remove],
  );

  const swapMultiCityAirports = useCallback(
    (index: number) => {
      const fromPath = `flights.${index}.from` as const;
      const toPath = `flights.${index}.to` as const;
      const from = form.getValues(fromPath) ?? EMPTY_AIRPORT;
      const to = form.getValues(toPath) ?? EMPTY_AIRPORT;

      form.setValue(fromPath, to, { shouldDirty: true, shouldTouch: true });
      form.setValue(toPath, from, { shouldDirty: true, shouldTouch: true });
      form.clearErrors([fromPath, toPath]);
    },
    [form],
  );

  const reorderFlights = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) {
        return;
      }

      move(fromIndex, toIndex);

      queueMicrotask(() => {
        const reorderedFlights = form.getValues("flights");
        const indicesToClear =
          FlightDateHelper.getInvalidMultiCityDateIndices(reorderedFlights);
        const clearedDateFieldNames = indicesToClear.map(
          (index) => `flights.${index}.departDate` as const,
        );

        indicesToClear.forEach((index) => {
          form.setValue(`flights.${index}.departDate`, null, {
            shouldDirty: true,
            shouldTouch: false,
            shouldValidate: false,
          });
        });

        if (clearedDateFieldNames.length > 0) {
          form.clearErrors(clearedDateFieldNames);
        }

        const dateFieldNames = reorderedFlights.map(
          (_, index) => `flights.${index}.departDate` as const,
        );
        const fieldsToRevalidate = dateFieldNames.filter(
          (fieldName) => !clearedDateFieldNames.includes(fieldName),
        );

        if (fieldsToRevalidate.length > 0) {
          void form.trigger(fieldsToRevalidate);
        }

        if (indicesToClear.length > 0) {
          const toastMessage = FlightDateHelper.getClearedDatesToastMessage(
            indicesToClear.length,
          );

          if (toastMessage) {
            toast.info(toastMessage.message, {
              description:
                "Dates after reordering were cleared to keep the trip sequence valid.",
            });
          }
        }
      });
    },
    [form, move],
  );

  const swapAirports = useCallback(() => {
    const from = form.getValues("flights.0.from") ?? EMPTY_AIRPORT;
    const to = form.getValues("flights.0.to") ?? EMPTY_AIRPORT;

    form.setValue("flights.0.from", to, { shouldDirty: true });
    form.setValue("flights.0.to", from, { shouldDirty: true });
    form.clearErrors(["flights.0.from", "flights.0.to"]);

    if (form.getValues("tripType") === "roundtrip") {
      const flights = form.getValues("flights");
      if (flights.length >= 2) {
        form.setValue("flights.1.from", from, { shouldDirty: true });
        form.setValue("flights.1.to", to, { shouldDirty: true });
        form.clearErrors(["flights.1.from", "flights.1.to"]);
      }
    }

    setSwapFlipped((v) => !v);
  }, [form]);

  const onSubmit = useCallback(
    async (data: TFlightSearchFormData) => {
      dispatch(toggleModifyBar(false));
      saveLastFlightSearch(data);
      startTransition(() => {
        const params = generateSearchParams(data);
        submitHandler?.({
          formdata: data,
          searchParams: params,
        });
      });
    },
    [dispatch, submitHandler],
  );

  const onInvalidSubmit = useCallback(
    (errors: FieldErrors<TFlightSearchFormData>) => {
      showSubmitErrorToast(errors);
    },
    [],
  );

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit, onInvalidSubmit)}
      className="w-full"
    >
      <div className="mb-5 grid grid-cols-[minmax(0,1fr)_78px_74px] items-center gap-2 md:flex md:gap-4 md:justify-between">
        <FlightFields.TripType form={form} />

        <div className="contents md:flex md:items-center md:gap-2">
          <FlightFields.Passenger form={form} />
          <FlightFields.CabinClass form={form} />
        </div>
      </div>

      {(tripType === "oneway" || tripType === "roundtrip") && (
        <div className="flex flex-col gap-5">
          <div
            className={cn(
              "grid items-start gap-3",
              tripType === "roundtrip"
                ? "grid-cols-1 md:grid-cols-[repeat(4,minmax(0,1fr))_52px] xl:grid-cols-[repeat(4,minmax(0,1fr))_56px]"
                : "grid-cols-1 md:grid-cols-[repeat(3,minmax(0,1fr))_52px] xl:grid-cols-[repeat(3,minmax(0,1fr))_56px]",
            )}
          >
            <div
              className={cn(
                "relative col-span-full grid gap-3 md:col-span-2 md:grid-cols-2",
              )}
            >
              <FlightFields.Airport
                form={form}
                name="flights.0.from"
                label="From"
                placeholder="Select departure"
              />

              <div className="pointer-events-none absolute right-3 top-[calc(50%-1rem)] z-10 flex items-center justify-center md:pointer-events-auto md:left-1/2 md:right-auto md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Swap origin and destination"
                  onClick={swapAirports}
                  className="pointer-events-auto size-8 rounded-full border border-border bg-muted text-muted-foreground shadow-none hover:bg-accent"
                >
                  <ArrowLeftRight
                    className={cn(
                      "h-4 w-4 transition-transform duration-300",
                      swapFlipped
                        ? "rotate-270 md:rotate-180"
                        : "rotate-90 md:rotate-0",
                    )}
                  />
                </Button>
              </div>

              <FlightFields.Airport
                form={form}
                name="flights.0.to"
                label="To"
                placeholder="Select destination"
              />
            </div>

            {tripType === "roundtrip" ? (
              <div className="grid grid-cols-2 gap-3 md:contents">
                <FlightFields.Date
                  form={form}
                  name="flights.0.departDate"
                  label="Depart"
                  disabledDate={DateUtils.isBeforeToday}
                  onDateChange={(d) => handleDateChange(0, d)}
                />

                <FlightFields.Date
                  form={form}
                  name="flights.1.departDate"
                  label="Return"
                  disabledDate={(date) => isDateDisabled(1, date)}
                  minDate={getMinDate(1)}
                />
              </div>
            ) : (
              <FlightFields.Date
                form={form}
                name="flights.0.departDate"
                label="Depart"
                disabledDate={DateUtils.isBeforeToday}
                onDateChange={(d) => handleDateChange(0, d)}
              />
            )}

            <Button
              type="submit"
              className={cn(
                "h-12 rounded-md bg-primary px-4 text-primary-foreground hover:bg-primary/90",
                tripType === "roundtrip"
                  ? "col-span-full md:col-span-1 md:size-13 md:min-w-13 md:px-0 xl:size-14 xl:min-w-14"
                  : "md:size-13 md:min-w-13 md:px-0 xl:size-14 xl:min-w-14",
              )}
              size="lg"
              disabled={isPending}
              aria-label="Search flights"
            >
              <Search className="h-5 w-5" />
              <span className="md:hidden">
                {isPending ? (
                  <TextShimmer duration={1.2}>Search</TextShimmer>
                ) : (
                  "Search"
                )}
              </span>
            </Button>
          </div>
        </div>
      )}

      {tripType === "multicity" && (
        <FlightMultiCityFields
          form={form}
          fields={fields}
          canRemoveMultiCityFlight={canRemoveMultiCityFlight}
          canAddFlight={FlightFormHelper.canAddTFlightSegment(fields)}
          isPending={isPending}
          onAddFlight={addFlight}
          onRemoveFlight={removeFlight}
          onSwapAirports={swapMultiCityAirports}
          onReorderFlights={reorderFlights}
          getFlightSegmentLabel={getFlightSegmentLabel}
          getMinDate={getMinDate}
          isDateDisabled={isDateDisabled}
          onDateChange={handleDateChange}
        />
      )}

      <div
        className={cn(
          "flex flex-wrap items-center gap-3 pt-8  md:gap-5",
          tripType === "multicity" && "pt-6",
        )}
        role="radiogroup"
        aria-label="Fare type"
      >
        {FARE_OPTIONS.map((fare) => {
          const isActive = selectedFare === fare.key;

          return (
            <label
              key={fare.key}
              className={cn(
                "inline-flex items-center gap-2 text-xs font-semibold transition-colors md:text-sm",
                isActive
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <input
                type="radio"
                name="fareType"
                value={fare.key}
                checked={isActive}
                onChange={() =>
                  form.setValue("fareType", fare.key as FlightFareType, {
                    shouldDirty: true,
                    shouldTouch: true,
                  })
                }
                className="size-4 accent-primary"
              />
              <span>{fare.label}</span>
            </label>
          );
        })}
      </div>
    </form>
  );
}
